/**
 * Maps backend support-API error responses to ngx-translate keys under
 * SUPPORT_ERROR.*. Two layers:
 *   1. Server-issued error codes (`response.body.error: 'PROJECT_CUSTOMER_MISMATCH'`)
 *      → SUPPORT_ERROR.PROJECT_CUSTOMER_MISMATCH key.
 *   2. Fallback by HTTP status family for unknown/missing codes.
 *
 * Returns a translation key (not a translated string) — the caller uses
 * TranslateService.instant(...) so the active locale is honoured.
 */

const ERROR_CODE_KEYS: Record<string, string> = {
  // 400
  EMPTY_PAYLOAD: 'SUPPORT_ERROR.EMPTY_PAYLOAD',
  // 409
  PROJECT_UNCHANGED: 'SUPPORT_ERROR.PROJECT_UNCHANGED',
  // 413
  PAYLOAD_TOO_LARGE: 'SUPPORT_ERROR.PAYLOAD_TOO_LARGE',
  // 422
  ORDER_ITEM_HAS_NO_PROJECT: 'SUPPORT_ERROR.ORDER_ITEM_HAS_NO_PROJECT',
  ORDER_ITEM_MISSING_CONTEXT: 'SUPPORT_ERROR.ORDER_ITEM_MISSING_CONTEXT',
  PROJECT_CUSTOMER_MISMATCH: 'SUPPORT_ERROR.PROJECT_CUSTOMER_MISMATCH',
  PROJECT_PRODUCT_MISMATCH: 'SUPPORT_ERROR.PROJECT_PRODUCT_MISMATCH',
  // 502
  BACKUP_FAILED: 'SUPPORT_ERROR.BACKUP_FAILED',
  PROJECT_FILE_DOWNLOAD_FAILED: 'SUPPORT_ERROR.PROJECT_FILE_DOWNLOAD_FAILED',
  ORDER_FILE_UPLOAD_FAILED: 'SUPPORT_ERROR.ORDER_FILE_UPLOAD_FAILED',
  // 500
  ORDER_STORAGE_NOT_RESOLVED: 'SUPPORT_ERROR.ORDER_STORAGE_NOT_RESOLVED',
  DB_TRANSACTION_FAILED: 'SUPPORT_ERROR.DB_TRANSACTION_FAILED',
  // 404
  CUSTOMER_NOT_FOUND: 'SUPPORT_ERROR.CUSTOMER_NOT_FOUND',
  ORDER_ITEM_NOT_FOUND: 'SUPPORT_ERROR.ORDER_ITEM_NOT_FOUND',
  PROJECT_NOT_FOUND: 'SUPPORT_ERROR.PROJECT_NOT_FOUND'
};

/**
 * Resolve a support-API error to an ngx-translate key. Pass anything —
 * an HttpErrorResponse, a thrown plain object, undefined — and get a key
 * back. Network failures (status 0) resolve to NETWORK; everything else
 * unrecognized falls through to GENERIC.
 */
export function supportErrorKey(err: unknown): string {
  // Try response body first (Symfony JSON: { error: 'CODE', message: '...' })
  const code = readErrorCode(err);
  if (code && ERROR_CODE_KEYS[code]) {
    return ERROR_CODE_KEYS[code];
  }

  const status = readStatus(err);
  if (status === 0) return 'SUPPORT_ERROR.NETWORK';
  if (status >= 500) return 'SUPPORT_ERROR.SERVER';
  if (status === 401 || status === 403) return 'SUPPORT_ERROR.FORBIDDEN';
  return 'SUPPORT_ERROR.GENERIC';
}

function readErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { error?: { error?: unknown }; data?: { error?: unknown } };
  // Angular HttpErrorResponse exposes the parsed body on `.error`.
  const body = e.error;
  if (body && typeof body === 'object') {
    const code = (body as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  // Some wrappers nest under `.data` instead.
  if (e.data && typeof e.data === 'object') {
    const code = (e.data as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

function readStatus(err: unknown): number {
  if (!err || typeof err !== 'object') return 0;
  const s = (err as { status?: unknown }).status;
  return typeof s === 'number' ? s : 0;
}
